import { useEffect, useMemo } from 'react';
import {
  GeoJSON as GeoJSONRaw,
  Marker as MarkerRaw,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';

import convex from '@turf/convex';
import buffer from '@turf/buffer';
import { featureCollection } from '@turf/helpers';

import type { SiteMapShare } from './useSiteMapShare';

// react-leaflet 3.x types are restrictive; cast to permissive so we can pass
// extra props/styles freely. Matches AirspaceMap.tsx convention.
const GeoJSON = GeoJSONRaw as any;
const Marker = MarkerRaw as any;

interface Props {
  share: SiteMapShare;
  showRings?: boolean;
  showLabels?: boolean;
  showImages?: boolean;
}

// ─── Producer-canonical constants (mirrored from studio/apps/producer/src/lib/constants.ts) ─
// Keep in sync if producer changes these. Drift here = visual divergence from
// the share page; values are tiny and stable so the cost of mirroring is low.
const COLOR_SAFETY_PERIMETER  = '#ffffff';
const COLOR_EMERGENCY_GEOFENCE = '#ef4444';
const COLOR_GEOFENCE          = '#3b82f6';
const COLOR_SHOW_HULL         = '#22c55e';
const COLOR_LAUNCH_ZONE       = '#eab308';
const COLOR_AUDIENCE_AREA     = '#38bdf8';

const GEOFENCE_OFFSET           = 5;   // meters: geofence → emergency geofence
const EMERGENCY_GEOFENCE_OFFSET = 10;  // meters: hull → emergency geofence
const DEFAULT_SAFETY_PERIMETER  = 50;  // meters
const IMAGE_BASE_SIZE_PX        = 48;  // producer renders placed images at 48*scale pixels

// Style table for non-ring polygons. `featureType` and `zoneType` keys come
// from producer's GeoJSON properties. The catch-all "default" applies to
// custom polygons that lack a specific zone type.
const POLYGON_STYLES: Record<string, L.PathOptions> = {
  [/* ZONE_TYPE_LAUNCH   */ 'launch_zone'  ]: { color: COLOR_LAUNCH_ZONE,   weight: 3, fillOpacity: 0.30 },
  [/* ZONE_TYPE_AUDIENCE */ 'audience_area']: { color: COLOR_AUDIENCE_AREA, weight: 3, fillOpacity: 0.15 },
  default:                                    { color: '#94a3b8',           weight: 2, fillOpacity: 0.10 },
};

// Approximate "meters → lat/lng offset" using equirectangular projection.
// Good enough for site-map scales (sub-km), avoids pulling in turf/destination.
function offsetMeters(centerLat: number, centerLng: number, dxMeters: number, dyMeters: number) {
  const metersPerDegLat = 111111;
  const metersPerDegLng = 111111 * Math.cos((centerLat * Math.PI) / 180);
  return {
    lat: centerLat + dyMeters / metersPerDegLat,
    lng: centerLng + dxMeters / metersPerDegLng,
  };
}

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Whitelist colors that can safely flow into an inline `style` attribute.
// Leaflet's divIcon HTML is inserted via innerHTML, so unsanitized values
// could break out of `style="..."` and inject `onerror` etc.
const COLOR_RE = /^(#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})|rgba?\(\s*[\d.,\s%]+\)|[a-z]+)$/i;
function safeColor(c: any, fallback: string): string {
  return typeof c === 'string' && COLOR_RE.test(c) ? c : fallback;
}
function safeFontSize(n: any, fallback: number): number {
  const x = Number(n);
  if (!Number.isFinite(x)) return fallback;
  return Math.max(8, Math.min(64, x));
}
function safeRotation(n: any): number {
  const x = Number(n);
  return Number.isFinite(x) ? x : 0;
}
function isHttpUrl(u: any): boolean {
  return typeof u === 'string' && /^https?:\/\//i.test(u);
}

/**
 * Sub-component for placed images. Producer renders these as DOM markers
 * sized at `48 * scale` PIXELS (constant on-screen size regardless of zoom),
 * not as geographic ImageOverlays. To match, we use a Marker with a divIcon
 * containing an <img>, with rotation via CSS transform.
 */
function PlacedImageMarker({ img }: { img: any }) {
  const url: string = img.thumbnail_url || img.image_url || img.url || img.src;
  const lat: number | null = img.lat ?? null;
  const lng: number | null = img.lng ?? null;
  const scale: number = Number(img.scale) || 1;
  const rotation: number = safeRotation(img.rotation);

  if (!url || !isHttpUrl(url) || lat == null || lng == null) return null;

  const size = Math.round(IMAGE_BASE_SIZE_PX * scale);

  return (
    <Marker
      position={[lat, lng]}
      icon={L.divIcon({
        className: 'site-map-placed-image',
        // Image rendered at producer's screen size; transform-origin defaults
        // to center, so rotation pivots around the marker's anchor point.
        html: `<img src="${escapeHtml(url)}" alt="" style="width:${size}px;height:${size}px;display:block;transform:rotate(${rotation}deg);transform-origin:center;pointer-events:none;user-select:none;" />`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      })}
      interactive={false}
      keyboard={false}
    />
  );
}

export function SiteMapLayer({
  share,
  showRings = true,
  showLabels = true,
  showImages = true,
}: Props) {
  const { state, geojson, textLabels, placedImages, mapSettings } = share;

  // Memoize ring + polygon derivations so we don't re-buffer on every parent render.
  const rings = useMemo(() => {
    if (state !== 'ready' || !geojson?.features?.length) return [];

    const features = geojson.features;
    const showBox = features.find((f: any) => f?.properties?.featureType === 'show_box');
    const launchZone = features.find((f: any) => f?.properties?.zoneType === 'launch_zone');
    if (!showBox) return [];

    let hull: any = showBox;
    if (launchZone) {
      try {
        hull = convex(featureCollection([showBox, launchZone] as any[])) ?? showBox;
      } catch {
        hull = showBox;
      }
    }

    // Producer's offsets are FIXED (not proportional). See
    // studio/apps/producer/src/lib/zones.ts:getZoneDefinitions.
    const sp: number = Number(mapSettings?.safetyPerimeter ?? mapSettings?.safety_perimeter ?? DEFAULT_SAFETY_PERIMETER);

    // Outermost-first paint order (back to front). Hull renders LAST so it
    // sits on top of all surrounding rings — matches producer's MapReadOnly.
    return [
      {
        id: 'safety_perimeter',
        g: buffer(hull as any, sp + EMERGENCY_GEOFENCE_OFFSET, { units: 'meters' }),
        color: COLOR_SAFETY_PERIMETER,
        weight: 3,
        fill: 0.05,
      },
      {
        id: 'emergency_geofence',
        g: buffer(hull as any, EMERGENCY_GEOFENCE_OFFSET, { units: 'meters' }),
        color: COLOR_EMERGENCY_GEOFENCE,
        weight: 3,
        fill: 0.10,
      },
      {
        id: 'geofence',
        g: buffer(hull as any, EMERGENCY_GEOFENCE_OFFSET - GEOFENCE_OFFSET, { units: 'meters' }),
        color: COLOR_GEOFENCE,
        weight: 3,
        fill: 0.10,
      },
      {
        id: 'show_hull',
        g: hull,
        color: COLOR_SHOW_HULL,
        weight: 3,
        fill: 0.20,
      },
    ];
  }, [state, geojson, mapSettings]);

  // Polygons OTHER than the show box. Launch zone renders both as a hull
  // input AND as a visible overlay (producer paints its yellow outline
  // separately on top of the green hull).
  const otherPolygons = useMemo(() => {
    if (state !== 'ready' || !geojson?.features?.length) return [];
    return geojson.features.filter((f: any) => {
      const ft = f?.properties?.featureType;
      const gt = f?.geometry?.type;
      // Skip the show box — it's the hull source, painted via `rings`.
      if (ft === 'show_box') return false;
      return gt === 'Polygon' || gt === 'MultiPolygon';
    });
  }, [state, geojson]);

  if (state !== 'ready') return null;

  return (
    <>
      {showRings &&
        rings.map((r) => (
          <GeoJSON
            key={r.id}
            data={r.g as any}
            style={{ color: r.color, weight: r.weight, fillColor: r.color, fillOpacity: r.fill }}
            interactive={false}
          />
        ))}

      {otherPolygons.map((f: any, i: number) => {
        const zt = f?.properties?.zoneType;
        // Per-feature color override wins (producer lets users recolor zones),
        // then zone-type style table, then catch-all default.
        const featureColor = f?.properties?.color;
        const styleKey = zt && POLYGON_STYLES[zt] ? zt : 'default';
        const s = POLYGON_STYLES[styleKey];
        const color = safeColor(featureColor, s.color as string);
        return (
          <GeoJSON
            key={f?.properties?.id ?? `poly-${i}`}
            data={f}
            style={{ ...s, color, fillColor: color }}
            interactive={false}
          />
        );
      })}

      {showLabels &&
        textLabels.map((label: any, i: number) => {
          const lat = label.lat;
          const lng = label.lng;
          if (lat == null || lng == null) return null;
          // Producer's TextLabel schema: { id, text, lng, lat, fontSize, color, rotation }
          const fontSize = safeFontSize(label.fontSize ?? label.font_size, 16);
          const color    = safeColor(label.color, '#ffffff');
          const rotation = safeRotation(label.rotation);
          const text     = String(label.text ?? '');
          // NB: omit iconSize/iconAnchor entirely — Leaflet auto-sizes the
          // wrapper to the inner HTML's natural size. Hard-coding [0,0]
          // collapses the icon and the label disappears.
          return (
            <Marker
              key={label.id ?? `label-${i}`}
              position={[lat, lng]}
              icon={L.divIcon({
                className: 'site-map-label',
                html: `<div style="font-size:${fontSize}px;color:${color};font-weight:600;text-shadow:0 0 4px rgba(0,0,0,0.85),0 0 2px rgba(0,0,0,0.85);white-space:nowrap;pointer-events:none;transform:translate(-50%,-50%) rotate(${rotation}deg);transform-origin:center;display:inline-block;">${escapeHtml(text)}</div>`,
              })}
              interactive={false}
              keyboard={false}
            />
          );
        })}

      {showImages &&
        placedImages.map((img: any, i: number) => (
          <PlacedImageMarker key={img.id ?? `img-${i}`} img={img} />
        ))}
    </>
  );
}

export default SiteMapLayer;
